import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import BottomNavigation from '../components/BottomNavigation';
import { ArrowLeft, Star, Clock, CheckCircle, XCircle, Edit2, Send } from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Pending Review', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
};

const MyReviews = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ rating: 5, comment: '', businessId: '' });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => { fetchReviews(); }, []);

  const fetchReviews = async () => {
    try {
      const res = await axiosInstance.get('/reviews/mine');
      setReviews(res.data.reviews || []);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (review) => {
    setEditingId(review.id);
    setEditForm({ rating: review.rating, comment: review.comment, businessId: review.businessId });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({ rating: 5, comment: '', businessId: '' }); };

  const submitEdit = async (reviewId) => {
    setSubmitting(true);
    try {
      await axiosInstance.put(`/reviews/${reviewId}`, editForm);
      setMessage({ type: 'success', text: 'Review resubmitted for moderation' });
      setEditingId(null);
      fetchReviews();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to resubmit' });
    } finally {
      setSubmitting(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 flex items-center gap-3">
        <button onClick={() => navigate('/profile')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5 rtl-flip" />
        </button>
        <h1 className="text-lg font-bold" data-testid="my-reviews-title">{t('myReviews', 'My Reviews')}</h1>
      </header>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {message && (
          <div className={`p-3 rounded-lg text-sm font-medium ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="review-message">
            {message.text}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12" data-testid="no-reviews">
            <Star className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{t('noReviewsYet', "You haven't written any reviews yet")}</p>
          </div>
        ) : (
          reviews.map((review) => {
            const cfg = STATUS_CONFIG[review.status] || STATUS_CONFIG.pending;
            const StatusIcon = cfg.icon;
            const isEditing = editingId === review.id;

            return (
              <div key={review.id} className="bg-white rounded-xl shadow-sm border p-4" data-testid={`review-card-${review.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900" data-testid={`review-business-name-${review.id}`}>
                      {review.businessName}
                    </h3>
                    <p className="text-xs text-gray-500">{new Date(review.createdAt).toLocaleDateString()}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border ${cfg.color}`} data-testid={`review-status-${review.id}`}>
                    <StatusIcon className="h-3 w-3" />
                    {cfg.label}
                  </span>
                </div>

                {!isEditing ? (
                  <>
                    <div className="flex items-center gap-0.5 mb-2" data-testid={`review-stars-${review.id}`}>
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className={`h-4 w-4 ${s <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                      ))}
                    </div>
                    <p className="text-sm text-gray-700 mb-2" data-testid={`review-comment-${review.id}`}>{review.comment}</p>

                    {review.status === 'rejected' && review.rejectionReason && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid={`review-rejection-${review.id}`}>
                        <p className="text-xs font-semibold text-red-800 mb-1">{t('rejectionReason', 'Rejection Reason')}:</p>
                        <p className="text-sm text-red-700">{review.rejectionReason}</p>
                      </div>
                    )}

                    {review.status === 'rejected' && (
                      <button
                        onClick={() => startEdit(review)}
                        className="mt-3 flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs font-medium hover:bg-purple-100 transition-colors"
                        data-testid={`review-edit-btn-${review.id}`}
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                        {t('editAndResubmit', 'Edit & Resubmit')}
                      </button>
                    )}
                  </>
                ) : (
                  <div className="space-y-3 mt-2" data-testid={`review-edit-form-${review.id}`}>
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(s => (
                        <button key={s} onClick={() => setEditForm({...editForm, rating: s})} type="button">
                          <Star className={`h-5 w-5 cursor-pointer ${s <= editForm.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={editForm.comment}
                      onChange={(e) => setEditForm({...editForm, comment: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows={3}
                      data-testid={`review-edit-textarea-${review.id}`}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => submitEdit(review.id)}
                        disabled={submitting || !editForm.comment.trim()}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
                        data-testid={`review-resubmit-btn-${review.id}`}
                      >
                        <Send className="h-3.5 w-3.5" />
                        {submitting ? 'Submitting...' : t('resubmit', 'Resubmit')}
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200"
                        data-testid={`review-cancel-edit-${review.id}`}
                      >
                        {t('cancel', 'Cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}

        <div className="text-center pt-4">
          <a href="/review-guidelines" className="text-sm text-purple-600 hover:text-purple-700 underline underline-offset-2" data-testid="review-guidelines-link">
            {t('reviewGuidelines', 'View review guidelines')}
          </a>
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default MyReviews;
